import katex from 'katex'

function decodeHtmlEntities(htmlString: string): string {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = htmlString;
    return textArea.value;
}

const rg = /<math>(.*?)<\/math>/gm;

const baseOptions = { output: 'html' } as const

// Imported documents habitually write unit symbols inside \text{} —
// "\text{\mu m}", "\text{\pm 5\%}". Real LaTeX tolerates it; KaTeX does not,
// because those commands are math-mode only and \text{} switches to text mode,
// so a single unit symbol fails the *whole* formula.
//
// Each entry expands to the plain Unicode character, wrapped in $...$ when we
// are already in text mode so KaTeX re-enters math mode and renders it from its
// own fonts with real metrics instead of substituting a fallback glyph.
const TEXT_MODE_SYMBOLS: Record<string, string> = {
    '\\mu': 'μ', '\\omega': 'ω', '\\Omega': 'Ω',
    '\\alpha': 'α', '\\beta': 'β', '\\gamma': 'γ', '\\delta': 'δ', '\\Delta': 'Δ',
    '\\epsilon': 'ε', '\\eta': 'η', '\\theta': 'θ', '\\lambda': 'λ', '\\pi': 'π',
    '\\rho': 'ρ', '\\sigma': 'σ', '\\tau': 'τ', '\\phi': 'φ',
    '\\pm': '±', '\\mp': '∓', '\\times': '×', '\\div': '÷', '\\cdot': '·',
    '\\le': '≤', '\\leq': '≤', '\\ge': '≥', '\\geq': '≥',
    '\\ne': '≠', '\\neq': '≠', '\\approx': '≈',
    '\\degree': '°', '\\circ': '∘', '\\infty': '∞',
    '\\rightarrow': '→', '\\leftarrow': '←',
}

// katex types the macro argument as a bare `object`; the parser passes its
// MacroExpander, whose `mode` tells us which mode the command was reached in.
type MacroExpander = { mode: 'math' | 'text' }

const rescueMacros: Record<string, (expander: object) => string> = {}
for (const [command, character] of Object.entries(TEXT_MODE_SYMBOLS)) {
    rescueMacros[command] = expander =>
        (expander as MacroExpander).mode === 'text' ? `$${character}$` : character
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

function renderFormula(formula: string): string {
    try {
        return katex.renderToString(formula, baseOptions)
    } catch {
        // Fall through: the rescue macros are applied only here, so nothing that
        // already renders today changes how it is rendered.
    }

    try {
        // throwOnError turns whatever is still unparseable into KaTeX's inline
        // error markup — the source text in red, carrying the parse error as a
        // tooltip — instead of an exception.
        //
        // Known rough edge: marker sometimes emits content that is not LaTeX at
        // all as a formula — a replay of 23 real conversions turned up
        // "<math>&</math>" six times — and a reviewer then sees a red "&" where
        // the document had a plain ampersand. Left as-is on purpose: it is
        // visible rather than silently wrong, and it points at a conversion
        // defect. If it becomes a nuisance, the fix is to render formulas with
        // no backslash in them as escaped plain text instead of error markup.
        return katex.renderToString(formula, { ...baseOptions, macros: rescueMacros, throwOnError: false, strict: 'ignore' })
    } catch {
        // renderToString can still throw on errors that aren't parse errors. This
        // runs inside a React render (StaticDocument.leafHtml) and the app has no
        // error boundary, so anything escaping here white-screens the whole
        // document over one malformed formula in supplier-supplied content.
        return escapeHtml(formula)
    }
}

// Entities are decoded INSIDE each formula only (KaTeX needs the raw "<", "&").
// Decoding the whole string turned escaped text back into live markup:
// "Ra&lt;/1.6um." became "Ra</1.6um.", which the HTML parser swallows as a
// bogus comment — text vanished — and "&lt;img onerror…&gt;" became real HTML.
export function katexnize(input: string): string {
    return input.replace(rg, (_, formula: string) => renderFormula(decodeHtmlEntities(formula)))
}
